import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { useStore } from '@/store';

export type LocationPermissionState =
  | 'granted'
  | 'prompt'
  | 'denied'
  | 'unknown';

export const getLocationPermissionState =
  async (): Promise<LocationPermissionState> => {
    try {
      if (Capacitor.isNativePlatform()) {
        const { location } = await Geolocation.checkPermissions();

        return location === 'prompt-with-rationale' ? 'prompt' : location;
      }

      // Web fallback
      if ('permissions' in navigator) {
        const result = await navigator.permissions.query({
          name: 'geolocation' as PermissionName,
        });

        return result.state;
      }

      return 'unknown';
    } catch {
      return 'unknown';
    }
  };

export const requestLocationPermission =
  async (): Promise<LocationPermissionState> => {
    try {
      const store = useStore();
      if (Capacitor.isNativePlatform()) {
        const { location } = await Geolocation.requestPermissions();
        const state =
          location === 'prompt-with-rationale' ? 'prompt' : location;

        store.location.permissions = state;

        return state;
      }

      // Web fallback
      if ('geolocation' in navigator) {
        return await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            () => {
              store.location.permissions = 'granted';
              resolve('granted');
            },
            () => {
              store.location.permissions = 'denied';
              resolve('denied');
            },
          );
        });
      }

      return 'unknown';
    } catch {
      return 'unknown';
    }
  };

export const syncLocationPermissionState = async () => {
  const state = await getLocationPermissionState();
  useStore().location.permissions = state;
  return state;
};
